import { Collection } from "intake_shared/src/enum";
import { ICommunicationSummary } from "intake_shared/src/types";
import db from "../utils/firestore";

export async function getCommunicationSummary(
    clientId: string,
    phoneNumber: string
): Promise<ICommunicationSummary | undefined> {
    if (!clientId || !phoneNumber) {
        return undefined;
    }

    const summaryDocument = await db
        .collection(Collection.COMMUNICATION_SUMMARIES)
        .doc(clientId)
        .get();

    if (!summaryDocument.exists) {
        return undefined;
    }

    const summary = summaryDocument.data() as ICommunicationSummary;
    return summary.phone_number === phoneNumber ? summary : undefined;
}

export function buildCommunicationSummary(
    storedSummary: ICommunicationSummary | undefined,
    summaryUpdate: ICommunicationSummary
): ICommunicationSummary {
    const matchingSummary =
        storedSummary?.phone_number === summaryUpdate.phone_number
            ? storedSummary
            : undefined;
    const summary: ICommunicationSummary = {
        client_id: summaryUpdate.client_id,
        phone_number: summaryUpdate.phone_number,
    };
    const smsCount = summaryUpdate.sms_count ?? matchingSummary?.sms_count;
    const callCount = summaryUpdate.call_count ?? matchingSummary?.call_count;
    const callCountTruncated =
        summaryUpdate.call_count !== undefined
            ? summaryUpdate.call_count_truncated
            : matchingSummary?.call_count_truncated;

    if (smsCount !== undefined) {
        summary.sms_count = smsCount;
    }
    if (callCount !== undefined) {
        summary.call_count = callCount;
    }
    if (callCountTruncated) {
        summary.call_count_truncated = true;
    }

    return summary;
}

export async function updateCommunicationSummary(
    summaryUpdate: ICommunicationSummary
): Promise<ICommunicationSummary> {
    const summaryReference = db
        .collection(Collection.COMMUNICATION_SUMMARIES)
        .doc(summaryUpdate.client_id);

    return db.runTransaction(async (transaction) => {
        const summaryDocument = await transaction.get(summaryReference);
        const storedSummary = summaryDocument.exists
            ? (summaryDocument.data() as ICommunicationSummary)
            : undefined;
        const summary = buildCommunicationSummary(storedSummary, summaryUpdate);

        if (
            storedSummary &&
            storedSummary.phone_number === summary.phone_number &&
            storedSummary.sms_count === summary.sms_count &&
            storedSummary.call_count === summary.call_count &&
            storedSummary.call_count_truncated === summary.call_count_truncated
        ) {
            return storedSummary;
        }

        transaction.set(summaryReference, summary);
        return summary;
    });
}
